// Service centralisé pour les appels API
import {
  shopsAPI,
  categoriesAPI,
  productsAPI,
  vendorsAPI,
  ordersAPI,
  bannersAPI,
  promoCodesAPI,
} from "../../api/endpoints";

// Service des boutiques
export const shopService = {
  // Obtenir une commande par ID
  async getOrder(id) {
    const response = await ordersAPI.getOrder(id);
    return response.data;
  },
  // Obtenir les commandes de la boutique
  async getCompanyOrders() {
    const response = await ordersAPI.getOrders();
    return response.data;
  },
  // Obtenir les boutiques les plus performantes
  async getShopTopPerformers() {
    const response = await shopsAPI.getShopTopPerformers();
    return response.data;
  },
  // Suspendre une boutique (motif optionnel envoyé au backend)
  async suspendShop(id, data = {}) {
    const response = await shopsAPI.suspendShop(id, data);
    return response.data;
  },

  // Obtenir une boutique par slug
  async getShopBySlug(slug) {
    const response = await shopsAPI.getShopBySlug(slug);
    return response.data;
  },

  // Mettre à jour le statut d'une boutique
  async updateShopStatus(id, status) {
    const response = await shopsAPI.updateShopStatus(id, status);
    return response.data;
  },

  async validateShop(id) {
    const response = await shopsAPI.validateShop(id);
    return response.data;
  },

  async rejectShop(id, rejectionReason) {
    const response = await shopsAPI.rejectShop(id, rejectionReason);
    return response.data;
  },

  // Obtenir une boutique par ID
  async getShop(id) {
    const response = await shopsAPI.getShop(id);
    return response.data;
  },

  // Obtenir les documents de la boutique (uploadés à la création)
  async getShopDocuments(id) {
    const response = await shopsAPI.getShopDocuments(id);
    return response.data;
  },

  // Créer une boutique
  async createShop(shopData) {
    const response = await shopsAPI.createShop(shopData);
    return response.data;
  },

  // Obtenir toutes les boutiques
  async getShops(filters = {}) {
    const response = await shopsAPI.getShops(filters);
    return response.data;
  },

  // Obtenir les statistiques des boutiques
  async getShopsStats() {
    const response = await shopsAPI.getShopsStats();
    return response.data;
  },

  // Mettre à jour une boutique
  async updateShop(id, shopData) {
    try {
      // Si shopData est un FormData, on doit utiliser l'endpoint avec multipart/form-data
      let response;
      if (shopData instanceof FormData) {
        response = await shopsAPI.updateShop(id, shopData);
      } else {
        response = await shopsAPI.updateShop(id, shopData);
      }
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la mise à jour de la boutique",
      };
    }
  },
};

// Service des catégories
export const categoryService = {
  // Obtenir toutes les catégories avec le nombre de produits
  async getCategoriesWithProductsCount(filters = {}) {
    const response = await categoriesAPI.getCategoriesWithProductsCount(filters);
    return response.data;
  },
  // Obtenir toutes les catégories
  async getCategories(filters = {}) {
    const response = await categoriesAPI.getCategories(filters);
    return response.data;
  },

  async getCategoryChildren(parentId) {
    const response = await categoriesAPI.getCategoryChildren(parentId);
    return response.data;
  },

  // Créer une catégorie
  async createCategory(categoryData) {
    try {
      const response = await categoriesAPI.createCategory(categoryData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la création de la catégorie",
      };
    }
  },

  // Mettre à jour une catégorie
  async updateCategory(id, categoryData) {
    try {
      const response = await categoriesAPI.updateCategory(id, categoryData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la mise à jour de la catégorie",
      };
    }
  },

  // Supprimer une catégorie
  async deleteCategory(id) {
    try {
      await categoriesAPI.deleteCategory(id);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la suppression de la catégorie",
      };
    }
  },
};

// Service des bannières
export const bannersService = {
  // Obtenir toutes les bannières (optionnellement filtrées par type)
  async getBanners(filters = {}) {
    try {
      const response = await bannersAPI.getBanners(filters);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la récupération des bannières",
      };
    }
  },

  // Obtenir une bannière par ID
  async getBanner(id) {
    try {
      const response = await bannersAPI.getBanner(id);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la récupération de la bannière",
      };
    }
  },

  // Créer une bannière
  async createBanner(formData) {
    try {
      const response = await bannersAPI.createBanner(formData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la création de la bannière",
      };
    }
  },

  // Mettre à jour une bannière
  async updateBanner(id, formData) {
    try {
      const response = await bannersAPI.updateBanner(id, formData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la mise à jour de la bannière",
      };
    }
  },

  // Mettre à jour le statut d'une bannière
  async updateBannerStatus(id, status) {
    try {
      const response = await bannersAPI.updateBannerStatus(id, status);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la mise à jour du statut",
      };
    }
  },

  // Mettre à jour la position d'une bannière
  async updateBannerPosition(id, position) {
    try {
      const response = await bannersAPI.updateBannerPosition(id, position);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la mise à jour de la position",
      };
    }
  },

  // Activer une bannière
  async activateBanner(id) {
    try {
      const response = await bannersAPI.activateBanner(id);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de l'activation de la bannière",
      };
    }
  },

  // Désactiver une bannière
  async deactivateBanner(id) {
    try {
      const response = await bannersAPI.deactivateBanner(id);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la désactivation de la bannière",
      };
    }
  },

  // Supprimer une bannière
  async deleteBanner(id) {
    try {
      await bannersAPI.deleteBanner(id);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la suppression de la bannière",
      };
    }
  },
};

// Service des produits
export const productService = {
  // Obtenir les statistiques des produits par boutique
  async getProductsStatsByShop(shopId) {
    const response = await productsAPI.getProductsStatsByShop(shopId);
    return response.data;
  },
  // Obtenir les produits similaires
  async getSimilarProducts(slug, filters = {}) {
    const response = await productsAPI.getSimilarProducts(slug, filters);
    return response.data;
  },
  // Obtenir un produit par slug
  async getProductBySlug(slug) {
    const response = await productsAPI.getProductBySlug(slug);
    return response.data;
  },
  // Obtenir tous les produits par boutique
  async getProductsByShopSlug(shopSlug, filters = {}) {
    const response = await productsAPI.getProductsByShopSlug(shopSlug, filters);
    return response.data;
},
  
  // Obtenir tous les produits
  async getProducts(filters = {}) {
    try {
      const response = await productsAPI.getProducts(filters);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération des produits' 
      };
    }
  },

  // Obtenir tous les produits par boutique
  async getProductsByShop(shopId, filters = {}) {
      const response = await productsAPI.getProductsByShop(shopId, filters);
      return response.data;
  },

  // Obtenir un produit
  async getProduct(id) {
    try {
      const response = await productsAPI.getProduct(id);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération du produit' 
      };
    }
  },

  // Créer un produit
  async createProduct(productData) {
    try {
      const response = await productsAPI.createProduct(productData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la création du produit' 
      };
    }
  },

  // Mettre à jour un produit
  async updateProduct(id, productData) {
    try {
      const response = await productsAPI.updateProduct(id, productData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Erreur lors de la mise à jour du produit:', error);
      console.error('Status:', error.response?.status);
      console.error('Headers:', error.response?.headers);
      console.error('Data:', error.response?.data);
      
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la mise à jour du produit' 
      };
    }
  },

  // Supprimer un produit — DELETE /products/:id (réponse peut contenir productInCarts, message, usersWithProductInCart, cartItemsCount)
  async deleteProduct(id) {
    try {
      const response = await productsAPI.deleteProduct(id);
      const data = response.data ?? response;
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Erreur lors de la suppression du produit',
      };
    }
  },

  // Suppression forcée — DELETE /products/:id/force (quand le produit est dans des paniers)
  async deleteProductForce(id) {
    try {
      await productsAPI.deleteProductForce(id);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Erreur lors de la suppression forcée du produit',
      };
    }
  },

  async likeProduct(id) {
    try {
      const response = await productsAPI.likeProduct(id);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Erreur lors de l'ajout du like sur le produit",
      };
    }
  },

  async unlikeProduct(id) {
    try {
      const response = await productsAPI.unlikeProduct(id);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Erreur lors du retrait du like sur le produit",
      };
    }
  },

  async getLikedProducts() {
    try {
      const response = await productsAPI.getLikedProducts();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Erreur lors de la récupération des produits likés",
      };
    }
  },
 
  // Créer un avis pour un produit
  async createProductReview(reviewData, files = []) {
    try {
      const response = await productsAPI.createProductReview(reviewData, files);
      const body = response.data ?? response;
      const ok =
        body?.statusCode === '201' ||
        body?.statusCode === 201 ||
        response.status === 201;
      if (!ok && body?.statusCode && String(body.statusCode).startsWith('4')) {
        return {
          success: false,
          error: body.message || "Erreur lors de la création de l'avis",
          data: body,
        };
      }
      return { success: true, data: body };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          "Erreur lors de la création de l'avis",
      };
    }
  },

  async updateProductReview(id, reviewData) {
    try {
      const response = await productsAPI.updateProductReview(id, reviewData);
      const body = response.data ?? response;
      if (body?.statusCode && String(body.statusCode).startsWith('4')) {
        return {
          success: false,
          error: body.message || "Erreur lors de la mise à jour de l'avis",
          data: body,
        };
      }
      return { success: true, data: body };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          "Erreur lors de la mise à jour de l'avis",
      };
    }
  },

  async getReviewEligibility(productId) {
    try {
      const response = await productsAPI.getReviewEligibility(productId);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          "Erreur lors de la vérification d'éligibilité",
      };
    }
  },

  async getAdminReviews(params = {}) {
    try {
      const response = await productsAPI.getAdminReviews(params);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Erreur lors de la récupération des avis',
      };
    }
  },

  async getShopReviews(params = {}) {
    try {
      const response = await productsAPI.getShopReviews(params);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Erreur lors de la récupération des avis',
      };
    }
  },

  async moderateReview(id, payload) {
    try {
      const response = await productsAPI.moderateReview(id, payload);
      const body = response.data ?? response;
      if (body?.statusCode && String(body.statusCode).startsWith('4')) {
        return {
          success: false,
          error: body.message || 'Erreur lors de la modération',
          data: body,
        };
      }
      return { success: true, data: body };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Erreur lors de la modération',
      };
    }
  },

  async likeReview(id) {
    try {
      const response = await productsAPI.likeReview(id);
      const body = response.data ?? response;
      if (body?.statusCode && String(body.statusCode).startsWith('4')) {
        return {
          success: false,
          error: body.message || "Erreur lors du like de l'avis",
          data: body,
        };
      }
      return { success: true, data: body };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message || "Erreur lors du like de l'avis",
      };
    }
  },

  async unlikeReview(id) {
    try {
      const response = await productsAPI.unlikeReview(id);
      const body = response.data ?? response;
      if (body?.statusCode && String(body.statusCode).startsWith('4')) {
        return {
          success: false,
          error: body.message || 'Erreur lors du retrait du like',
          data: body,
        };
      }
      return { success: true, data: body };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message || 'Erreur lors du retrait du like',
      };
    }
  },

  // Obtenir les avis d'un produit
  async getProductReviews(productId) {
    try {
      const response = await productsAPI.getProductReviews(productId);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération des avis' 
      };
    }
  },
};

// Service des vendeurs
export const vendorService = {
  // Obtenir tous les vendeurs
  async getVendors(filters = {}) {
    try {
      const response = await vendorsAPI.getVendors(filters);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération des vendeurs' 
      };
    }
  },

  // Obtenir un vendeur par ID
  async getVendor(id) {
    try {
      const response = await vendorsAPI.getVendor(id);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération du vendeur' 
      };
    }
  },

  // Mettre à jour le statut d'un vendeur
  async updateVendorStatus(id, status) {
    try {
      const response = await vendorsAPI.updateVendorStatus(id, status);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la mise à jour du statut' 
      };
    }
  },

  // Obtenir les statistiques des vendeurs
  async getVendorStats() {
    try {
      const response = await vendorsAPI.getVendorStats();
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération des statistiques' 
      };
    }
  },
};

export const promoService = {
  async validate(payload) {
    const response = await promoCodesAPI.validate(payload);
    return response.data;
  },
  async listByShop(shopId) {
    const response = await promoCodesAPI.listByShop(shopId);
    return response.data;
  },
  async create(data) {
    const response = await promoCodesAPI.create(data);
    return response.data;
  },
  async update(id, data) {
    const response = await promoCodesAPI.update(id, data);
    return response.data;
  },
  async remove(id) {
    const response = await promoCodesAPI.remove(id);
    return response.data;
  },
};

